import { LegalPage } from "../../components/LegalPage";

export function RegulatoryAiMethodology() {
  return (
    <LegalPage
      title="Regulatory AI methodology"
      eyebrow="Trust center"
      description="WeConsult Rx Regulatory AI Assistant methodology: Voyage AI legal-domain embeddings (voyage-law-2), Bedrock RAG, zero-hallucination cite-or-refuse, server-side quote verification, caching strategy, and disclaimer posture."
      path="/trust/regulatory-ai-methodology"
      effective="2026-05-31"
    >
      <p>
        The WeConsult Rx Regulatory Assistant is a retrieval-augmented
        question-answering surface over public-domain regulatory text. It is
        engineered around a single design principle:
        <strong>
          {" "}
          the model cannot answer regulatory questions from parametric memory.
          It only quotes, with verification, from retrieved verbatim chunks.
        </strong>
      </p>

      <h2>Architecture</h2>
      <ol>
        <li>
          <strong>Embedding model.</strong> Voyage AI{" "}
          <code>voyage-law-2</code> — a 1024-dimensional embedding model tuned
          on legal and regulatory text. Indexed once per source revision.
        </li>
        <li>
          <strong>Retrieval.</strong> User query is embedded, top-K candidate
          chunks are pulled from Postgres pgvector with cosine similarity, then
          re-ranked with a hybrid score (cosine weighted 0.7 + token-overlap
          BM25-style weighted 0.3).
        </li>
        <li>
          <strong>Synthesis.</strong> Top-10 chunks are sent to AWS Bedrock
          (Anthropic Claude). The system prompt strictly forbids parametric
          memory: the model must produce a JSON object with{" "}
          <code>answer_md</code> and a list of citations, each citation
          containing the exact verbatim quote from the cited chunk.
        </li>
        <li>
          <strong>Quote verification.</strong> Before returning to the user,
          the server verifies every citation: the{" "}
          <code>exact_quote</code> must appear (case-insensitive, whitespace
          normalized) inside the cited chunk's <code>chunk_text</code>. If a
          citation cannot be verified, it is dropped. If <em>all</em>{" "}
          citations are dropped, the answer is replaced with the safety
          fallback.
        </li>
        <li>
          <strong>Refusal.</strong> If retrieval returns no relevant chunks,
          the safety fallback is surfaced immediately — Bedrock is not
          invoked. This means the model has no opportunity to hallucinate.
        </li>
      </ol>

      <h2>Sources indexed</h2>
      <ul>
        <li>
          <strong>HIPAA</strong> — 45 CFR Parts 160, 162, 164 (Privacy /
          Security / Breach)
        </li>
        <li>
          <strong>DEA</strong> — 21 CFR Parts 1300–1321 (Controlled Substances)
        </li>
        <li>
          <strong>FDA pharmacy</strong> — 21 CFR Parts 207, 211, 503A
        </li>
        <li>
          <strong>CMS SNF</strong> — 42 CFR Part 483 (Skilled Nursing Facility
          Requirements)
        </li>
        <li>
          <strong>CMS ASC</strong> — 42 CFR Part 416 (Ambulatory Surgical
          Center Conditions)
        </li>
        <li>
          <strong>CMS ESRD</strong> — 42 CFR Part 494 (Dialysis Facility
          Conditions)
        </li>
        <li>
          <strong>CMS SOM Appendix PP</strong> — F-tag interpretive guidance
        </li>
        <li>
          <strong>State pharmacy rules</strong> — initial coverage for FL, TX,
          CA, NY, OH, PA, IL, GA, MI (more added on request)
        </li>
        <li>
          <strong>AHCA Florida</strong> — 59A-4 (LTC) and 58A-5 (ALF)
        </li>
      </ul>

      <h2>Cache strategy + freshness</h2>
      <ul>
        <li>
          <strong>Static-7d</strong> — federal eCFR responses cached for 7
          days; sources are re-ingested weekly to capture revisions.
        </li>
        <li>
          <strong>Mixed-1h</strong> — state pharmacy + CMS SOM interpretive
          guidance cached for 1 hour. Shorter TTL acknowledges interpretive
          guidance changes more often than federal rule text.
        </li>
        <li>
          <strong>Corpus version tag.</strong> Cache keys include the maximum
          <code>version_date</code> of indexed sources. When any source is
          re-ingested with a newer version_date, all client queries hash
          differently and miss the old cache — guaranteeing freshness.
        </li>
      </ul>

      <h2>What the assistant will not do</h2>
      <ul>
        <li>
          It will not <strong>opine on legal advice</strong>. Every response
          carries the disclaimer:{" "}
          <em>
            Educational reference only. Not legal advice. Verify with counsel
            and current state board rules.
          </em>
        </li>
        <li>
          It will not <strong>invent regulatory text</strong>. Every quoted
          claim is verified verbatim against a chunk before delivery.
        </li>
        <li>
          It will not <strong>summarize from memory</strong>. If retrieval
          returns no relevant chunks, the safety fallback is surfaced — Bedrock
          is not invoked.
        </li>
      </ul>

      <h2>Consultant pharmacist remains responsible</h2>
      <p>
        The assistant is a research tool. The consultant pharmacist — and
        outside counsel where applicable — remain responsible for any
        regulatory decision. The assistant exists to make canonical sources
        immediately retrievable, not to replace professional judgment.
      </p>

      <h2>Audit + privacy posture</h2>
      <ul>
        <li>
          Raw query bodies are <strong>never stored</strong>. The audit log
          records only the SHA-256 hash of the normalized query, the
          jurisdiction/source filters, token counts, and timing. Queries are
          public regulatory questions, not PHI, but we treat them with the
          same defense-in-depth posture in case a user incidentally mentions a
          resident name.
        </li>
        <li>
          The cache stores AI-synthesized answers and the verified verbatim
          chunks they quote. The cache is system-wide (not tenant-scoped) —
          there is no tenant or PHI data in it.
        </li>
        <li>
          Per-tenant audit telemetry is available to operators for usage and
          cost tracking. The query hash provides a deterministic identifier
          for support without revealing the literal text.
        </li>
      </ul>

      <h2>Independent review roadmap</h2>
      <p>
        We plan annual third-party review of the citation verification gate
        and chunk corpus. Findings will be summarized in this page. Until the
        first independent review is published, the assistant remains a
        research aid, not a substitute for counsel.
      </p>
    </LegalPage>
  );
}
